import React, { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import {
	Button,
	Confirm,
	GridContainer,
	Input,
	InputType,
	Modal,
	Select,
	SpinnerOverlay,
	Tooltip,
	WarningMessage } from '@components/common';

import { TGenerateSiteId } from '@hooks/useManageSites';
import { useManageSitesOptions } from '@hooks/useManageSitesOptions';

import { formattedToNumber } from '@utils/formatter';

import { IManageSiteValuesDTO } from '@interfaces/cells/dto/cellsDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';

import { EVendorsValues } from '@enums/common';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { modalCloseConfirm } from '@constants/modals';
import { regExpDecimalAndHyphen } from '@constants/regExp';
import {
	coordsAreas,
	createSiteCoordsWarningMessage,
	generateSiteIdTooltip,
	linkedSiteVendorTooltip,
	manageSiteFormAreas,
	vendorTooltip,
} from './constants';

import { addSchema, editSchema } from './schema';

import styles from './ManageSiteForm.module.scss';

export interface IManageSiteFormProps {
	toggleSiteModal: () => void;
	generateSiteId: TGenerateSiteId;
	submitHandler: (values: IManageSiteValuesDTO) => void;
	isLoadingForm: boolean;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	editableSite: Record<string, any> | null;
	isLinkMode?: boolean;
}

const ManageSiteForm = ({
	editableSite,
	generateSiteId,
	isLinkMode = false,
	isLoadingForm,
	setIsDirtyFormModal,
	submitHandler,
	toggleSiteModal,
}: IManageSiteFormProps) => {
	const isEmptyTechnologies = editableSite ? !editableSite?.technologies?.length : true;
	const [isConfirmModal, setIsConfirmModal] = useState(false);
	const [nextSiteId, setNextSiteId] = useState(0);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => generateSiteId({ setSiteId: setNextSiteId }), []);

	const siteIdGuideline = `The most recent site with Site_ID: ${nextSiteId > 0 ? nextSiteId - 1 : 0}`;

	const { vendorsOptions, siteTypesOptions } = useManageSitesOptions();

	const [isEditMode, setIsEditMode] = useState(false);

	useEffect(() => {
		setIsEditMode(!!editableSite);
	}, [editableSite, isEditMode]);

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		control,
		setValue,
		watch,
	} = useForm<FieldValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(isEditMode ? editSchema : addSchema),
		defaultValues: {
			siteId: editableSite?.siteId || '',
			vendor: null,
			lat: editableSite?.lat || '',
			lng: editableSite?.lng || '',
			siteType: null,
		},
	});

	useEffect(() => {
		if (isLinkMode)
			setValue('vendor', vendorsOptions?.find((vendor: TSelectOption) => vendor?.value === EVendorsValues.limenet));
		else
			setValue('vendor', vendorsOptions?.find((vendor: TSelectOption) => vendor?.value === editableSite?.vendor?.name));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendorsOptions?.length]);

	useEffect(() => {
		setValue('siteType', siteTypesOptions?.find((type: TSelectOption) => type?.label === editableSite?.siteType?.title));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteTypesOptions?.length]);

	const isEmptyCoords = !(watch('lat') && watch('lng'));

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	const onSubmit = (values: FieldValues) => {
		const siteData = {
			siteId: formattedToNumber(values.siteId),
			siteType: values.siteType.label,
			vendor: values.vendor.value,
			lat: formattedToNumber(values.lat),
			lng: formattedToNumber(values.lng),
		};

		submitHandler(siteData);
	};

	const handleCloseModals = () => {
		if (isDirty) setIsConfirmModal(true);
		else toggleSiteModal();
	};

	const handleCloseConfirmModal = () => setIsConfirmModal(false);

	const setSiteId = (id: number) => setValue('siteId', id, { shouldValidate: true });

	const onClickAutoHandler = () => setSiteId(nextSiteId);

	const vendorTooltipContent = editableSite?.assetId
		? linkedSiteVendorTooltip
		: isEmptyTechnologies
			? undefined
			: vendorTooltip;

	return (
		<GridContainer
			columns={2}
			customClassName={styles.manageSiteFormContainer}
			gap='15px 20px'
			gridTemplateAreas={manageSiteFormAreas}
		>
			{isLoadingForm && <SpinnerOverlay />}
			<Modal
				isOpen={isConfirmModal}
				onClose={handleCloseConfirmModal}
			>
				<Confirm
					onClose={handleCloseConfirmModal}
					onSubmit={toggleSiteModal}
					{...modalCloseConfirm}
				/>
			</Modal>

			<div className={styles.siteIdContainer}>
				<div className={styles.siteId}>
					<Input
						errorMessage={errors?.siteId?.message}
						isReadOnly={!isEmptyTechnologies}
						label='Site_ID'
						name='siteId'
						placeholder='Enter Site_ID'
						register={register}
						required
						setValue={setValue}
						tooltipContent={siteIdGuideline}
						type={InputType.integer}
					/>
					<Tooltip content={generateSiteIdTooltip}>
						<div>
							<Button
								className={styles.generateButton}
								disabled={!isEmptyTechnologies}
								onClick={onClickAutoHandler}
								text='Auto'
								withoutBackground
							/>
						</div>
					</Tooltip>
				</div>
			</div>
			<div className={styles.vendor}>
				<Select
					control={control}
					disabled={isLinkMode || !isEmptyTechnologies || editableSite?.assetId}
					errorMessage={errors.vendor?.message}
					isError={!!errors?.vendor}
					isReadOnly={!isEmptyTechnologies}
					label='Vendor'
					name='vendor'
					options={vendorsOptions}
					placeholder='Select vendor'
					required
					tooltipContent={vendorTooltipContent}
				/>
			</div>
			<GridContainer
				columns={2}
				customClassName={styles.coords}
				gap='5px 20px'
				gridTemplateAreas={coordsAreas}
			>
				<div className={styles.lat}>
					<Input
						errorMessage={errors?.lat?.message}
						label='Latitude'
						name='lat'
						placeholder='Enter latitude'
						regexValidation={regExpDecimalAndHyphen}
						register={register}
						required={isEditMode}
						setValue={setValue}
					/>
				</div>
				<div className={styles.lng}>
					<Input
						errorMessage={errors?.lng?.message}
						label='Longitude'
						name='lng'
						placeholder='Enter longitude'
						regexValidation={regExpDecimalAndHyphen}
						register={register}
						required={isEditMode}
						setValue={setValue}
					/>
				</div>
				<div className={styles.warning}>
					{isEmptyCoords && <WarningMessage content={createSiteCoordsWarningMessage} />}
				</div>
			</GridContainer>
			<div className={styles.siteType}>
				<Select
					control={control}
					errorMessage={(errors.siteType?.message || (errors.siteType as any)?.value?.message) as string}
					isClearable
					isCreatable
					isError={!!errors?.siteType}
					label='Site Type/Civil Model'
					name='siteType'
					options={siteTypesOptions}
					placeholder='Select or enter type/civil model'
					required
				/>
			</div>
			<div className={styles.submitButtons}>
				<Button
					onClick={handleCloseModals}
					text='Cancel'
					withoutBackground
				/>
				<Button
					onClick={handleSubmit(onSubmit)}
					text={editableSite ? 'Save' : 'Add'}
				/>
			</div>
		</GridContainer>
	);
};

export default ManageSiteForm;
